// LivestreamExploreMoreModal.jsx
import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import FeedComponent from '../container/FeedComponent.js';
import ExploreMoreHint from '../component/ExploreMoreHint.jsx';
import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';
import SideDrawer from '../component/SideDrawer.jsx';
import {
  SheetHeader,
  SheetHeaderTitle,
  SheetHeaderCloseButton,
} from '../component/SheetComponentUnits.jsx';

import usePreviousValue from '../hook/usePreviousValue.js';

import { NULL_FUNCTION, EMPTY_ARRAY } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { ButtonId, ModalId } from '../resource/mixpanel.js';

import { color, iPhoneXSeriesFooterPaddingInPWA } from '../style/variables.js';

import EmptyIconSrouce from '../../img/img_empty_nofollower.svg';

const EXPLORE_MORE_HINT_AUTO_CLOSE_DELAY = 3000;
const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();
const MOBILE_CARD_SIZE = 148;
const tabs = [
  {
    titleI18nKey: 'user_livestream_v2',
    trackingButtonId: ButtonId.LiveStream.ButtonExploreMoreLivestreamPopular,
  },
  {
    titleI18nKey: 'title_following_list',
    trackingButtonId: ButtonId.LiveStream.ButtonExploreMoreLivestreamFollowing,
  },
];

const tabStyle = {
  flex: 'none',
  marginRight: '16px',
  paddingRight: '0px',
  paddingLeft: '0px',
};
const labelStyle = {
  margin: '0px',
  paddingTop: '18px',
  paddingBottom: '8px',
  fontSize: '12px',
  fontWeight: '400',
  lineHeight: '1.5',
};

export const LivestreamExploreMoreModal = ({
  isOpen = false,
  containerRef,
  isEnabled = true,
  onOpen = NULL_FUNCTION,
  onClose = NULL_FUNCTION,
  onDragStateChange = NULL_FUNCTION,
  activeStreamId = '',
  popularCategory = '',
  followingCategory = '',
  isPopularIdsEmpty = false,
  isFollowingIdsEmpty = false,
  popularSelectPath = EMPTY_ARRAY,
  followingSelectPath = EMPTY_ARRAY,
  shouldDisplayExploreMoreHint = false,
  setShouldDisplayExploreMoreHint = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const prevActiveStreamId = usePreviousValue(activeStreamId);

  const handleClose = useCallback(() => {
    setShouldDisplayExploreMoreHint({
      shouldDisplayExploreMoreHint: false,
    });
    onClose();
  }, [setShouldDisplayExploreMoreHint, onClose]);

  const handleHintClick = useCallback(
    event => {
      event.stopPropagation();
      setShouldDisplayExploreMoreHint({
        shouldDisplayExploreMoreHint: false,
      });
    },
    [setShouldDisplayExploreMoreHint]
  );

  const usingCategory = 0 === tab ? popularCategory : followingCategory;
  const selectPath = 0 === tab ? popularSelectPath : followingSelectPath;
  const isEmpty = 0 === tab ? isPopularIdsEmpty : isFollowingIdsEmpty;
  const isHintVisible = shouldDisplayExploreMoreHint && isOpen;

  useEffect(() => {
    if (prevActiveStreamId !== activeStreamId && prevActiveStreamId != null) {
      onClose();
    }
  }, [activeStreamId, prevActiveStreamId, onClose]);

  useEffect(() => {
    if (!isHintVisible) return;
    const timer = setTimeout(() => {
      setShouldDisplayExploreMoreHint({
        shouldDisplayExploreMoreHint: false,
      });
    }, EXPLORE_MORE_HINT_AUTO_CLOSE_DELAY);
    return () => clearTimeout(timer);
  }, [isHintVisible, setShouldDisplayExploreMoreHint]);

  return (
    <>
      <SideDrawer
        isOpen={isOpen}
        containerRef={containerRef}
        isEnabled={isEnabled}
        trackingModalId={ModalId.LiveStream.ModalExploreMoreLivestream}
        width="90vw"
        isTransparentMask
        onOpen={onOpen}
        onClose={handleClose}
        onDragStateChange={onDragStateChange}
      >
        <ExploreMorePanel>
          <SheetHeader>
            <SheetHeaderTitle>
              {t('livestream_explore_more_title', {
                ns: TranslationNamespace.LIVESTREAM,
              })}
            </SheetHeaderTitle>
            <SheetHeaderCloseButton
              trackingButtonId={
                ButtonId.LiveStream.ButtonExploreMoreLivestreamClose
              }
              onClick={handleClose}
            />
          </SheetHeader>
          <Body>
            <TabsWrapper>
              <Tabs
                value={tab}
                onChange={(event, value) => {
                  setTab(value);
                }}
                isScrollable
              >
                {tabs.map((item, index) => (
                  <Tab
                    key={index}
                    data-element_id={item.trackingButtonId}
                    value={index}
                    style={tabStyle}
                    labelStyle={labelStyle}
                    label={t(item.titleI18nKey, {
                      ns: TranslationNamespace.LIVESTREAM,
                    })}
                  />
                ))}
              </Tabs>
            </TabsWrapper>
            <FeedWrapper isEmptyLayout={isEmpty}>
              {isEmpty ? (
                <>
                  <EmptyIcon src={EmptyIconSrouce} alt="empty" />
                  <EmptyText>
                    {t('it_is_empty_here', {
                      ns: TranslationNamespace.GENERAL,
                    })}
                  </EmptyText>
                </>
              ) : (
                <FeedComponent
                  category={usingCategory}
                  index={0}
                  shouldDisplayHeader={false}
                  selectPath={selectPath}
                  useWindow={false}
                  mobileCardSize={MOBILE_CARD_SIZE}
                  intersectionThreshold={0}
                />
              )}
            </FeedWrapper>
          </Body>
        </ExploreMorePanel>
      </SideDrawer>
      {isHintVisible && <ExploreMoreHint onClick={handleHintClick} />}
    </>
  );
};

LivestreamExploreMoreModal.displayName = 'LivestreamExploreMoreModal';

LivestreamExploreMoreModal.propTypes = {
  isOpen: PropTypes.bool,
  containerRef: PropTypes.object,
  isEnabled: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onDragStateChange: PropTypes.func,
  activeStreamId: PropTypes.string,
  popularCategory: PropTypes.string,
  followingCategory: PropTypes.string,
  isPopularIdsEmpty: PropTypes.bool,
  isFollowingIdsEmpty: PropTypes.bool,
  popularSelectPath: PropTypes.array,
  followingSelectPath: PropTypes.array,
  shouldDisplayExploreMoreHint: PropTypes.bool,
  setShouldDisplayExploreMoreHint: PropTypes.func,
};

const ExploreMorePanel = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  background-color: ${color.neutral[0]};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TabsWrapper = styled.div`
  border-bottom: 1px solid ${color.neutral['20_alpha_40']};
`;

const FeedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  padding-bottom: ${isIphoneXSeriesPWA
    ? iPhoneXSeriesFooterPaddingInPWA
    : 16}px;
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
  ${({ isEmptyLayout }) =>
    isEmptyLayout &&
    css`
      padding-top: 32px;
      padding-left: 60px;
      padding-right: 60px;
      text-align: center;
    `};
`;

const EmptyIcon = styled.img`
  margin: 0 auto 12px;
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const EmptyText = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: ${color.neutral[30]};
`;

export default LivestreamExploreMoreModal;
